import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModerationVisitHeader from "../../components/ModerationVisitHeader";
import { useUnits } from "../../context/UnitsContext";
import "./ReviewVisitRequest.css";
import { useSubject } from "../../context/SubjectContext";
import { useCenter } from "../../context/CenterContext";
import ModalErrorBox from "../../components/form/ModalErrorBox";
import { useApi } from "../../context/ApiClient";
import { useSession } from "../../context/SessionApi";

const formatDate = (isoStr) => {
  if (!isoStr) return null;
  const dateObj = new Date(isoStr);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = dateObj.toLocaleString("en-GB", { month: "long" });
  const year = dateObj.getFullYear();
  return `Week beginning ${day} ${month} ${year}`;
};

const ReviewVisitRequest = () => {
  const sessions = useSession();

  const api = useApi(process.env.REACT_APP_VAF_BASE_URL);
  const { state: formData } = useLocation();
  const [errorOpen, setErrorOpen] = useState(false);
  const navigate = useNavigate();
  const { units } = useUnits();
  const { subjectData } = useSubject();
    const { centerNumber } = useCenter();
    const subjectId = subjectData?.selectedSubjectId;
  const selectedUnits = (formData.selectedUnits || [])
    .map((code) => units.find((u) => u.assessmentCode === code))
    .filter(Boolean);
  const consortiumYes =
    formData.isConsortium === "Y" || formData.isConsortium === "yes";
  let finalFormData = { ...formData,  subjectName: subjectData?.selectedSubject };
 
  if (formData.visitArrangementId) {
    finalFormData["updatedBy"] = "test";
  } else {
    finalFormData["insertedBy"] = "test";
  }
  const handleSubmitRequest = async () => {
    try {
      // Make a clean safe copy with only valid codes:
      finalFormData.selectedUnits = (finalFormData.selectedUnits || []).filter(
        (code) => code && code.length <= 4
      );
      if (formData.visitArrangementId) {
        await api.put(
          `updateModerationVisit/${formData.visitArrangementId}`,
          finalFormData
        );
      } else {
        await api.post(`submitModerationVisit`, finalFormData);
      }

      // Clear form data from localStorage as the form steps are complete
      localStorage.removeItem(`moderationFormData_${centerNumber}_${subjectId}`);

      
      const subject = subjectData?.selectedSubject || "subject";
      const subjectSlug = subject
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+/, "")   // remove leading underscores
        .replace(/_+$/, "");  // remove trailing underscores
      navigate(`/dashboard/visit_form/submitted/${subjectSlug}`);
    } catch (error) {
      setErrorOpen(true);
    }
  };

  const handleEditRequest = () => {
    navigate(-1);
  };

  const getModerationWindowLabel = (sessions, sessionId) => {
    if (!sessions || !sessionId) return sessionId;

    const session = sessions.find((s) => s.sessionId === Number(sessionId));
    if (!session) return sessionId;

    const yearLabel = `${session.sessionYear}/${String(
      session.sessionYear + 1
    ).slice(-2)}`;

    const windowLabel =
      session.window === 1
        ? "Moderation window 1 (February series)"
        : session.window === 2
        ? "Moderation window 2 (June series)"
        : "Moderation window";

    return `${windowLabel} - ${yearLabel}`;
  };

  return (
    <div className="page-layout">
      {/* Show ErrorBox if errorOpen */}
      {errorOpen && <ModalErrorBox onClose={() => setErrorOpen(false)} />}

      <ModerationVisitHeader
        isReviewPage={true}
        onSubmitRequest={handleSubmitRequest}
        onEditRequest={handleEditRequest}
      />

      <div className="mt-5 review-min-height">
        <h2 className="review-title">Review and submit your visit request</h2>

        <div className="review-box box">
          <div className="columns review-columns">
            {/* Left Column */}
            <div className="column review-col">
              <div className="block review-block">
                <p>
                  <strong>Moderation type:</strong>
                </p>
                <p>
                  {formData.moderationType === "Face-to-Face"
                    ? "Face-to-face"
                    : formData.moderationType}
                </p>
              </div>
              <div className="block review-block">
                <p>
                  <strong>Units being moderated:</strong>
                </p>

                {(selectedUnits || [])
                  .slice()
                  .sort((a, b) =>
                    a.assessmentCode.localeCompare(b.assessmentCode)
                  )
                  .map((unit) => (
                    <p key={unit.assessmentCode}>
                      {unit.assessmentCode} {unit.assessmentName}
                    </p>
                  ))}
              </div>
              <div className="block review-block">
                <p>
                  <strong>Moderation window:</strong>
                </p>
                <p>
                  {getModerationWindowLabel(
                    sessions,
                    formData.moderationWindow
                  )}
                </p>
              </div>
              <div className="block review-block">
                <p>
                  <strong>Preferred dates:</strong>
                </p>
                {(formData.preferredDates || [])
                  .slice()
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((date, idx) => (
                    <p key={idx}>{formatDate(date)}</p>
                  ))}
              </div>
            </div>
            {/* Right Column */}
            <div className="column review-col">
              <div className="block review-block">
                <p>
                  <strong>Part of consortium: </strong>
                  {consortiumYes ? "Yes" : "No"}
                </p>
                {consortiumYes && (
                  <div className="notification review-notification">
                    Make sure your centre has completed the centre consortium
                    arrangements form within the JCQ Centre Admin Portal (CAP)
                    for this moderation window.
                  </div>
                )}
              </div>
              <div className="block review-block">
                <p>
                  <strong>Contact(s):</strong>
                </p>
                <p>
                  {formData.primaryFirstName} {formData.primaryLastName} (
                  {formData.primaryEmailId})<br />
                  {formData.primaryTelephoneNo}
                </p>
                {formData.isSecondaryContact && formData.secondaryFirstName && (
                  <>
                    <br />
                    <p>
                      {formData.secondaryFirstName} {formData.secondaryLastName}{" "}
                      ({formData.secondaryEmailId})<br />
                      {formData.secondaryTelephoneNo}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewVisitRequest;
